
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Presentation, Star, Users, TrendingUp, Mic, Video } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface PitchSession {
  id: string;
  title: string;
  type: 'practice' | 'feedback' | 'recorded';
  status: 'scheduled' | 'completed' | 'in-progress';
  date: string;
  feedback?: string[];
}

const PitchPlaygroundPro: React.FC = () => {
  const { toast } = useToast();
  const [sessions, setSessions] = useState<PitchSession[]>([
    {
      id: '1',
      title: 'EcoTech Startup Pitch',
      type: 'feedback',
      status: 'completed',
      date: '2024-01-15',
      feedback: ['Great passion!', 'Need clearer market size', 'Strong problem identification']
    },
    {
      id: '2',
      title: 'AI Study Assistant App',
      type: 'practice',
      status: 'scheduled',
      date: '2024-01-20'
    }
  ]);

  const proFeatures = [
    {
      icon: Star,
      title: 'AI-Powered Feedback',
      description: 'Get instant feedback on your pitch delivery, content, and presentation style.',
      active: true
    },
    {
      icon: Users,
      title: 'Expert Mentor Reviews',
      description: 'Receive detailed feedback from industry experts and successful entrepreneurs.',
      active: true
    },
    {
      icon: Video,
      title: 'Video Recording & Analysis',
      description: 'Record your pitches and get detailed analysis of your performance.',
      active: true
    },
    {
      icon: TrendingUp,
      title: 'Progress Tracking',
      description: 'Track your improvement over time with detailed analytics.',
      active: true
    }
  ];

  const startPitchSession = (type: string) => {
    const newSession: PitchSession = {
      id: Date.now().toString(),
      title: `New ${type} Session`,
      type: type as 'practice' | 'feedback' | 'recorded',
      status: 'in-progress',
      date: new Date().toISOString().split('T')[0]
    };
    
    setSessions([newSession, ...sessions]);
    toast({
      title: "Pitch Session Started!",
      description: `Your ${type} session has begun. Premium features are now available.`
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Presentation className="h-5 w-5 text-blue-500" />
            Pitch Playground PRO
          </CardTitle>
          <CardDescription>
            Advanced pitch development tools with AI feedback and expert mentorship.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Pro Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {proFeatures.map((feature, index) => (
              <div key={index} className="border rounded-lg p-4 bg-blue-50">
                <div className="flex items-start gap-3">
                  <feature.icon className="h-5 w-5 text-blue-500 mt-1" />
                  <div>
                    <h4 className="font-medium flex items-center gap-2">
                      {feature.title}
                      <Badge className="bg-green-500 text-white text-xs">PRO</Badge>
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="border rounded-lg p-4 bg-gradient-to-r from-blue-50 to-purple-50">
            <h3 className="font-semibold mb-3">Start New Pitch Session</h3>
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={() => startPitchSession('practice')}
                className="bg-blue-500 hover:bg-blue-600"
              >
                <Mic className="h-4 w-4 mr-2" />
                Practice Session
              </Button>
              <Button 
                onClick={() => startPitchSession('feedback')}
                variant="outline"
              >
                <Users className="h-4 w-4 mr-2" />
                Get Expert Feedback
              </Button>
              <Button 
                onClick={() => startPitchSession('recorded')}
                variant="outline"
              >
                <Video className="h-4 w-4 mr-2" />
                Record & Analyze
              </Button>
            </div>
          </div>

          {/* Recent Sessions */}
          <div>
            <h3 className="font-semibold mb-3">Recent Pitch Sessions</h3>
            <div className="space-y-3">
              {sessions.map((session) => (
                <div key={session.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium">{session.title}</h4>
                    <div className="flex gap-2">
                      <Badge variant="outline">{session.type}</Badge>
                      <Badge className={
                        session.status === 'completed' ? 'bg-green-500' :
                        session.status === 'in-progress' ? 'bg-blue-500' :
                        'bg-orange-500'
                      }>
                        {session.status}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{session.date}</p>
                  
                  {session.feedback && (
                    <div className="mt-3">
                      <h5 className="text-sm font-medium mb-2">Expert Feedback:</h5>
                      <div className="space-y-1">
                        {session.feedback.map((feedback, i) => (
                          <div key={i} className="text-sm bg-green-50 p-2 rounded">
                            • {feedback}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end mt-3">
                    <Button size="sm" variant="outline">
                      View Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PitchPlaygroundPro;
